import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMapPin, FiUser } from 'react-icons/fi';
import DateTimePicker from '../components/common/DateTimePicker';
import axios from 'axios';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { kundliAPI } from '../services/api';
import './Kundli.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// South Indian Chart layout: fixed rashi positions
// Standard SI chart: Meenam at top-left, goes clockwise
// | Min(11) | Mes(0)  | Ris(1)  | Mit(2)  |
// | Kum(10) |       center      | Kat(3)  |
// | Mak(9)  |       center      | Sim(4)  |
// | Dan(8)  | Vri(7)  | Tul(6)  | Kan(5)  |
const SI_POSITIONS = [
  { row: 0, col: 1 }, // 0  Mesham (Aries)
  { row: 0, col: 2 }, // 1  Rishabam (Taurus)
  { row: 0, col: 3 }, // 2  Mithunam (Gemini)
  { row: 1, col: 3 }, // 3  Kadagam (Cancer)
  { row: 2, col: 3 }, // 4  Simmam (Leo)
  { row: 3, col: 3 }, // 5  Kanni (Virgo)
  { row: 3, col: 2 }, // 6  Thulam (Libra)
  { row: 3, col: 1 }, // 7  Viruchigam (Scorpio)
  { row: 3, col: 0 }, // 8  Dhanusu (Sagittarius)
  { row: 2, col: 0 }, // 9  Magaram (Capricorn)
  { row: 1, col: 0 }, // 10 Kumbam (Aquarius)
  { row: 0, col: 0 }, // 11 Meenam (Pisces)
];

const RASHI_LABELS = [
  'Mes', 'Ris', 'Mit', 'Kad', 'Sim', 'Kan',
  'Thu', 'Vir', 'Dha', 'Mag', 'Kum', 'Mee'
];

const PLANET_ABBREVS = {
  'ASC': 'As', 'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma', 'Mercury': 'Me',
  'Jupiter': 'Ju', 'Venus': 'Ve', 'Saturn': 'Sa', 'Rahu': 'Ra', 'Ketu': 'Ke'
};

/** South Indian Chart SVG Component */
const SouthIndianChart = ({ rasiChart, lagnaRashi, title }) => {
  const cellSize = 90;
  const width = cellSize * 4;
  const height = cellSize * 4;

  // Build planet map per rashi index
  const planetsByRashi = {};
  if (rasiChart) {
    for (let house = 1; house <= 12; house++) {
      const rashiIndex = (lagnaRashi + house - 1) % 12;
      const planetsInHouse = rasiChart[house] || [];
      if (planetsInHouse.length > 0) {
        planetsByRashi[rashiIndex] = planetsInHouse.map(p =>
          PLANET_ABBREVS[p.replace('(R)', '')] + (p.includes('(R)') ? 'ᴿ' : '')
        );
      }
    }
  }

  return (
    <div className="si-chart-container">
      {title && <h4 className="chart-title">{title}</h4>}
      <svg viewBox={`0 0 ${width} ${height}`} className="si-chart-svg">
        {/* Background */}
        <rect x="0" y="0" width={width} height={height} fill="#fffaf5" stroke="#ff6f00" strokeWidth="2" rx="4" />

        {/* Draw 12 rashi cells */}
        {SI_POSITIONS.map((pos, rashiIdx) => {
          const x = pos.col * cellSize;
          const y = pos.row * cellSize;
          const isLagna = rashiIdx === lagnaRashi;
          const planets = planetsByRashi[rashiIdx] || [];

          return (
            <g key={rashiIdx}>
              <rect
                x={x} y={y} width={cellSize} height={cellSize}
                fill={isLagna ? 'rgba(255,111,0,0.08)' : 'transparent'}
                stroke="#ddd" strokeWidth="1"
              />
              {/* Rashi label */}
              <text x={x + 5} y={y + 14} fontSize="10" fill="#999" fontFamily="Poppins">
                {RASHI_LABELS[rashiIdx]}
              </text>
              {/* Lagna marker */}
              {isLagna && (
                <text x={x + cellSize - 5} y={y + 14} fontSize="10" fill="#ff6f00" fontFamily="Poppins" textAnchor="end" fontWeight="bold">
                  Asc
                </text>
              )}
              {/* Planets */}
              {planets.map((p, i) => (
                <text
                  key={i}
                  x={x + cellSize / 2}
                  y={y + 32 + i * 16}
                  fontSize="13"
                  fill={p.includes('ᴿ') ? '#c62828' : '#1a1a1a'}
                  fontFamily="Poppins"
                  fontWeight="600"
                  textAnchor="middle"
                >
                  {p}
                </text>
              ))}
            </g>
          );
        })}

        {/* Center area label */}
        <rect x={cellSize} y={cellSize} width={cellSize * 2} height={cellSize * 2} fill="#fffaf5" stroke="#ff6f00" strokeWidth="1.5" rx="2" />
        <text x={width / 2} y={height / 2 - 5} fontSize="14" fill="#ff6f00" fontFamily="Cinzel" fontWeight="700" textAnchor="middle">
          {title || 'Rasi Chart'}
        </text>
        <text x={width / 2} y={height / 2 + 12} fontSize="10" fill="#999" fontFamily="Poppins" textAnchor="middle">
          South Indian
        </text>
      </svg>
    </div>
  );
};

/** Planet positions table - matches VB.NET format */
const PlanetTable = ({ planets }) => {
  if (!planets || planets.length === 0) return null;
  return (
    <div className="planets-table">
      <h4>Planetary Positions</h4>
      <table>
        <thead>
          <tr>
            <th>Planet</th>
            <th>Position(degrees)</th>
            <th>Sign</th>
            <th>Star</th>
            <th>Padam</th>
            <th>Lord</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((p, i) => (
            <tr key={i} className={p.isRetrograde ? 'retro-row' : ''}>
              <td>
                <span className="planet-symbol">{p.symbol}</span>
                {p.name}{p.isRetrograde ? ' (R)' : ''}
              </td>
              <td>{p.positionDegree}</td>
              <td>{p.rashi}</td>
              <td>{p.nakshatra}</td>
              <td>{p.pada}</td>
              <td>{p.nakshatraLord}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Dasha-Bhukti 120 Year Analysis */
const DashaSection = ({ dasha }) => {
  const [expandedDasha, setExpandedDasha] = useState(null);

  if (!dasha) return null;

  const now = new Date();

  // Auto-expand current maha dasha
  const currentMahaIndex = dasha.allMahaDashas
    ? dasha.allMahaDashas.findIndex(md => {
        const s = new Date(md.startDate);
        const e = new Date(md.endDate);
        return now >= s && now < e;
      })
    : -1;

  const toggleExpand = (index) => {
    setExpandedDasha(expandedDasha === index ? null : index);
  };

  // Use auto-expanded current if nothing manually selected
  const activeExpanded = expandedDasha !== null ? expandedDasha : currentMahaIndex;

  return (
    <div className="dasha-section">
      <h4>Vimshottari Dasha - 120 Year Analysis</h4>

      {dasha.currentMahaDasha && (
        <div className="current-dasha-card">
          <div className="current-label">Current Period</div>
          <div className="current-info">
            <span className="dasha-lord">{dasha.currentMahaDasha.lord} Maha Dasha</span>
            {dasha.currentAntarDasha && (
              <span className="antar-lord"> &rarr; {dasha.currentAntarDasha.lord} Bhukti</span>
            )}
          </div>
          <div className="dasha-dates">
            {dasha.currentMahaDasha.start} to {dasha.currentMahaDasha.end}
          </div>
        </div>
      )}

      <div className="dasha-full-table">
        {dasha.allMahaDashas && dasha.allMahaDashas.map((md, i) => {
          const start = new Date(md.startDate);
          const end = new Date(md.endDate);
          const isCurrent = now >= start && now < end;
          const isPast = now >= end;
          const isExpanded = activeExpanded === i;

          return (
            <div key={i} className={`dasha-block ${isCurrent ? 'current' : ''} ${isPast ? 'past' : ''}`}>
              <div className="dasha-block-header" onClick={() => toggleExpand(i)}>
                <div className="dasha-block-left">
                  <span className={`dasha-expand-icon ${isExpanded ? 'expanded' : ''}`}>&#9654;</span>
                  <span className="dasha-block-lord">{md.lord}</span>
                  <span className="dasha-block-years">{md.durationYears}y</span>
                </div>
                <div className="dasha-block-right">
                  <span className="dasha-block-dates">{md.startDate} to {md.endDate}</span>
                  {isCurrent && <span className="dasha-current-badge">Running</span>}
                </div>
              </div>

              {isExpanded && md.antarDashas && (
                <div className="bhukti-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Bhukti Lord</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {md.antarDashas.map((ad, j) => {
                        const adStart = new Date(ad.startDate);
                        const adEnd = new Date(ad.endDate);
                        const isAdCurrent = now >= adStart && now < adEnd;
                        const isAdPast = now >= adEnd;

                        // Format duration as years, months, days
                        const years = Math.floor(ad.durationYears);
                        const remMonths = (ad.durationYears - years) * 12;
                        const months = Math.floor(remMonths);
                        const days = Math.round((remMonths - months) * 30);
                        const durationStr = `${years}Y ${months}M ${days}D`;

                        return (
                          <tr key={j} className={`${isAdCurrent ? 'bhukti-current' : ''} ${isAdPast ? 'bhukti-past' : ''}`}>
                            <td className="bhukti-lord">
                              {md.lord}-{ad.lord}
                              {isAdCurrent && <span className="bhukti-running">Running</span>}
                            </td>
                            <td>{ad.startDate}</td>
                            <td>{ad.endDate}</td>
                            <td>{durationStr}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Basic horoscope details summary */
const BasicDetails = ({ horoscope }) => {
  if (!horoscope) return null;

  const details = [
    { label: 'Latitude', value: horoscope.birthDetails.latitude },
    { label: 'Longitude', value: horoscope.birthDetails.longitude },
    { label: 'Lagna', value: horoscope.lagna.rashi },
    { label: 'Lagna Lord', value: horoscope.lagna.lord },
    { label: 'Birth Sign', value: horoscope.birthSign, highlight: true, highlightLabel: true },
    { label: 'Nakshatra (Padam)', value: horoscope.nakshatra, highlight: true, highlightLabel: true },
    { label: 'Tithi', value: horoscope.tithi?.name },
    { label: 'Paksham', value: horoscope.tithi?.paksha },
    { label: 'Ayanamsa', value: `Lahiri - ${horoscope.ayanamsa}\u00B0` },
    { label: 'Balance Dasha', value: horoscope.balanceDashaFormatted },
  ];

  return (
    <div className="basic-details">
      <h4>Basic Details</h4>
      <div className="details-grid">
        {details.map((d, i) => (
          <div key={i} className="detail-row">
            <span className={`detail-label${d.highlightLabel ? ' highlight' : ''}`}>{d.label}</span>
            <span className={`detail-value${d.highlight ? ' highlight' : ''}`}>{d.value || '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Shadbala Strength bars */
const ShadbalaSection = ({ shadbala, manglik }) => {
  if (!shadbala || shadbala.length === 0) return null;

  const maxTotal = Math.max(...shadbala.map(s => s.total));

  return (
    <div className="shadbala-section">
      <h4>Shadbala (Planetary Strength)</h4>
      <div className="strength-bars">
        {shadbala.map((s, i) => (
          <div key={i} className="strength-row">
            <span className="strength-planet">{s.planet}</span>
            <div className="strength-bar-bg">
              <div
                className="strength-bar-fill"
                style={{ width: `${(s.total / maxTotal) * 100}%` }}
              ></div>
            </div>
            <span className="strength-value">{s.total.toFixed(2)}</span>
          </div>
        ))}
      </div>

      {manglik && (
        <div className={`manglik-badge ${manglik.isManglik ? 'is-manglik' : 'no-manglik'}`}>
          <strong>Manglik Dosha:</strong> {manglik.isManglik ? `Yes - ${manglik.severity}` : 'No'}
          {manglik.exceptions && manglik.exceptions.length > 0 && (
            <span className="manglik-exceptions"> ({manglik.exceptions.join(', ')})</span>
          )}
        </div>
      )}
    </div>
  );
};

const Kundli = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    date: '',
    time: '',
    place: ''
  });
  const [horoscope, setHoroscope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // City search
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const suggestionsRef = useRef(null);
  const resultRef = useRef(null);
  const hydrated = useHydrated();

  // Scroll to result on mobile after horoscope is generated
  useEffect(() => {
    if (horoscope && resultRef.current && window.innerWidth <= 900) {
      setTimeout(() => {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [horoscope]);

  // Pre-fill form data from home page navigation and auto-generate
  useEffect(() => {
    if (location.state?.formData) {
      const navFormData = location.state.formData;
      const navLocation = location.state.selectedLocation;
      setFormData(navFormData);

      // Clear navigation state so it doesn't re-trigger
      window.history.replaceState({}, document.title);

      // Auto-generate horoscope
      if (navFormData.date && navFormData.time) {
        (async () => {
          try {
            setLoading(true);
            setError('');

            let city = navLocation;

            // If no pre-resolved location, search for the city
            if (!city && navFormData.place) {
              const cityRes = await axios.get(`${API_BASE_URL}/panchang/cities`, {
                params: { q: navFormData.place }
              });
              const cities = cityRes.data.data || [];
              if (cities.length > 0) {
                city = cities[0];
              }
            }

            if (city) {
              setSelectedLocation(city);
              const displayName = city.state
                ? `${city.city}, ${city.state}, ${city.country}`
                : `${city.city}, ${city.country}`;
              setFormData(prev => ({ ...prev, place: displayName }));

              // Generate horoscope with resolved coordinates
              const payload = {
                name: navFormData.name,
                gender: navFormData.gender,
                date: navFormData.date,
                time: navFormData.time,
                place: city.city,
                lat: city.latitude,
                lon: city.longitude,
                tz: city.timezone,
                state: city.state,
                country: city.country,
              };

              const response = await kundliAPI.generateKundli(payload);
              if (response.data.success) {
                setHoroscope(response.data.data);

              } else {
                setError(response.data.error || 'Failed to generate horoscope');
              }
            } else {
              setError('City not found. Please search and select a city from suggestions.');
            }
          } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to generate horoscope');
          } finally {
            setLoading(false);
          }
        })();
      }
    }
  }, [location.state]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // City autocomplete
  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, {
        params: { q: query }
      });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.place.length >= 2 && !selectedLocation) {
        searchCities(formData.place);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.place, searchCities, selectedLocation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'place') {
      setSelectedLocation(null);
    }
  };

  const handleCitySelect = (city) => {
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setFormData(prev => ({ ...prev, place: displayName }));
    setSelectedLocation(city);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setHoroscope(null);

    try {
      const payload = {
        name: formData.name,
        gender: formData.gender,
        date: formData.date,
        time: formData.time,
        place: formData.place,
      };

      if (selectedLocation) {
        payload.lat = selectedLocation.latitude;
        payload.lon = selectedLocation.longitude;
        payload.tz = selectedLocation.timezone;
        payload.place = selectedLocation.city;
        payload.state = selectedLocation.state;
        payload.country = selectedLocation.country;
      }

      const response = await kundliAPI.generateKundli(payload);
      if (response.data.success) {
        setHoroscope(response.data.data);
      } else {
        setError(response.data.error || 'Failed to generate horoscope');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate horoscope');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kundli-page">
      <SEO
        title="online horoscope generator"
        description="This is an Online Horoscope generator which can be used to generate extensive Horoscopes, Birth Charts, The 120-year Vimshottari Dasha system dictates planetary periods (Mahadasha) and sub-periods (Bhukti) based on your birth Nakshatra (constellation), starting with the ruling planet of that Nakshatra  for free"
        keywords="kundli, birth chart, vedic horoscope, rasi chart, navamsa, dasha, planetary positions, free kundli online"
        canonicalPath="/online-horoscope-generator"
        hreflang={[
          { lang: 'en', path: '/online-horoscope-generator' },
          { lang: 'ta', path: '/jathagam-in-tamil' },
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Free Kundli Generator",
          "applicationCategory": "LifestyleApplication",
          "operatingSystem": "Web Browser",
          "description": "Generate detailed Vedic birth chart with planetary positions, dashas, and predictions",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {"@type": "Question", "name": "Is the free Kundli generator accurate?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, AstroEpanchang uses precise Swiss Ephemeris calculations with Lahiri Ayanamsa \u2014 the same standard used by professional astrologers in India. Accuracy depends on entering the correct birth time, as even a few minutes difference can change the Lagna (Ascendant) and Dasha period."}},
            {"@type": "Question", "name": "What information do I need to generate my Kundli?", "acceptedAnswer": {"@type": "Answer", "text": "You need your exact date of birth, time of birth (as accurate as possible \u2014 hospital records are best), and place of birth (city/town). The birth place is used to determine geographic coordinates and timezone for precise planetary calculations."}},
            {"@type": "Question", "name": "What is the difference between Rasi chart and Navamsa chart?", "acceptedAnswer": {"@type": "Answer", "text": "The Rasi chart (D-1) is the primary birth chart showing planetary positions at birth. The Navamsa chart (D-9) divides each sign into 9 equal parts and is used for marriage compatibility and spiritual analysis. Both charts are equally important in Vedic astrology \u2014 the Rasi chart shows potential, while the Navamsa reveals how that potential manifests in life."}},
            {"@type": "Question", "name": "What is Manglik Dosha in a Kundli?", "acceptedAnswer": {"@type": "Answer", "text": "Manglik Dosha (Kuja Dosha) occurs when Mars is placed in the 1st, 2nd, 4th, 7th, 8th, or 12th house of the birth chart. It is believed to affect marriage harmony. Approximately 50% of people have some form of Manglik Dosha. When both partners have Manglik Dosha, the effects are believed to neutralize each other."}},
            {"@type": "Question", "name": "Can I get a Kundli without knowing my exact birth time?", "acceptedAnswer": {"@type": "Answer", "text": "A partial chart can be prepared using only date and place of birth, but it will lack accurate Lagna, house placements, and precise Dasha starting point. For complete and reliable predictions, the exact birth time is essential. If unknown, an experienced astrologer can perform birth time rectification using key life events."}},
            {"@type": "Question", "name": "What is a Nakshatra and why is it important?", "acceptedAnswer": {"@type": "Answer", "text": "Nakshatras are 27 lunar mansions used in Vedic astrology, each spanning 13 degrees 20 minutes of the zodiac. Your birth Nakshatra (the constellation where the Moon was placed at birth) determines your Vimshottari Dasha sequence, personality traits, and compatibility with other Nakshatras. It is one of the most fundamental elements in Vedic astrology for marriage matching, naming ceremonies, and spiritual practices."}}
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>Free <span className="gradient-text">Horoscope</span> Generator</h1>
          <p>Generate your detailed Vedic birth chart with planetary positions, dashas, and predictions</p>
        </motion.div>

        <div className="kundli-content">
          <motion.div
            className="kundli-form-section"
            initial={hydrated ? { opacity: 0, x: -30 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="form-card">
              <h3>Enter Birth Details</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    <FiUser className="input-icon" /> Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <DateTimePicker
                  date={formData.date}
                  time={formData.time}
                  onDateChange={(val) => setFormData(prev => ({ ...prev, date: val }))}
                  onTimeChange={(val) => setFormData(prev => ({ ...prev, time: val }))}
                />

                <div className="form-group form-lang-selector">
                  <label>Language</label>
                  <select
                    value="en"
                    onChange={(e) => {
                      if (e.target.value === 'ta') {
                        navigate('/jathagam-in-tamil', { state: { formData, selectedLocation } });
                      }
                    }}
                  >
                    <option value="en">English</option>
                    <option value="ta">தமிழ் (Tamil)</option>
                  </select>
                </div>

                <div className="form-group city-search-group" ref={suggestionsRef}>
                  <label>
                    <FiMapPin className="input-icon" /> Place of Birth
                  </label>
                  <input
                    type="text"
                    name="place"
                    value={formData.place}
                    onChange={handleChange}
                    placeholder="Search city..."
                    required
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="city-suggestions">
                      {suggestions.map((city, i) => (
                        <li key={i} onClick={() => handleCitySelect(city)}>
                          {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Generating...' : 'Generate Horoscope'}
                </button>

                {error && <div className="form-error">{error}</div>}
              </form>
            </div>
          </motion.div>

          {horoscope && (
            <motion.div
              ref={resultRef}
              className="kundli-result-section"
              initial={hydrated ? { opacity: 0, x: 30 } : false}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="chart-card">
                <div className="chart-header">
                  <div>
                    <h3>Birth Chart - {horoscope.name}</h3>
                    <p className="birth-info">
                      {horoscope.birthDetails.date} | {horoscope.birthDetails.time} | {horoscope.birthDetails.place}
                    </p>
                  </div>
                </div>

                <BasicDetails horoscope={horoscope} />

                <div className="section-block">
                  <SouthIndianChart
                    rasiChart={horoscope.rasiChart}
                    lagnaRashi={horoscope.lagna.rashiIndex}
                    title="Rasi Chart"
                  />
                  <PlanetTable planets={horoscope.planets} />
                </div>

                <div className="section-block">
                  <SouthIndianChart
                    rasiChart={horoscope.navamsaChart}
                    lagnaRashi={horoscope.navamsaLagnaRashi}
                    title="Navamsa (D-9)"
                  />
                  {horoscope.divisionalCharts?.navamsa && (
                    <div className="planets-table">
                      <h4>Navamsa Positions</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>Planet</th>
                            <th>Navamsa Rashi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {horoscope.divisionalCharts.navamsa.map((p, i) => (
                            <tr key={i}>
                              <td>{p.planet}</td>
                              <td>{p.rashi}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="section-block">
                  <DashaSection dasha={horoscope.dasha} />
                </div>

                <div className="section-block">
                  <ShadbalaSection
                    shadbala={horoscope.shadbala}
                    manglik={horoscope.manglik}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Free Vedic Birth Chart (Kundli) Generator Online</h2>
          <p>AstroEpanchang's free horoscope generator creates your detailed Vedic birth chart (Kundli) based on your exact date, time, and place of birth. Our calculations use the Lahiri Ayanamsa and authentic Vedic astrology principles to provide accurate Rasi charts, Navamsa (D-9) charts, planetary positions with nakshatra and pada details.</p>
          <p>The birth chart reveals your Lagna (Ascendant), Moon sign, birth star (Nakshatra), and the placement of all nine planets (Navagraha) across the 12 houses. Our tool also includes Vimshottari Dasha analysis covering the full 120-year cycle with Maha Dasha and Antar Dasha (Bhukti) periods, along with Shadbala planetary strength calculations and Manglik Dosha assessment.</p>
          <p>Whether you want to understand your personality, career prospects, marriage compatibility, or life predictions, your Kundli is the foundation of all Vedic astrological analysis. Generate your free horoscope now and explore the cosmic blueprint of your life.</p>

          <h3>What is a Kundli (Vedic Birth Chart)?</h3>
          <p>A Kundli, also known as a Janam Patrika or Janma Kundali, is a detailed astrological chart prepared based on the exact date, time, and place of a person's birth. In Vedic astrology, the Kundli maps the precise positions of the Sun, Moon, and all visible planets at the moment of birth across 12 houses (Bhavas) and 12 zodiac signs (Rashis). This cosmic snapshot helps astrologers understand a person's character, destiny, career, relationships, and life events. The 12 houses represent different life domains — health, wealth, siblings, home, children, enemies, spouse, longevity, fortune, profession, income, and liberation.</p>

          <h3>How to Read Your Vedic Birth Chart</h3>
          <p>Reading a Kundli involves analyzing several key components. The Lagna (Ascendant) — the zodiac sign rising on the eastern horizon at birth — determines the layout of all 12 houses. The Moon sign (Rashi) indicates your emotional nature and is used for daily predictions. Each house governs a specific life domain: the 1st house represents self and personality, the 7th house governs marriage, the 10th house covers career and profession, and the 4th house relates to home and mother. Planetary aspects (Drishti) and conjunctions further modify the results of each house. The strength of each planet (Shadbala) determines how powerfully it influences your life outcomes.</p>

          <h3>Vimshottari Dasha — Planetary Periods in Vedic Astrology</h3>
          <p>The Vimshottari Dasha is a unique 120-year timing system in Vedic astrology, divided among nine planets. Each Maha Dasha (major period) is governed by a planet: Sun (6 years), Moon (10 years), Mars (7 years), Rahu (18 years), Jupiter (16 years), Saturn (19 years), Mercury (17 years), Ketu (7 years), and Venus (20 years). The sequence begins with the planet ruling your birth Nakshatra. Within each Maha Dasha are Antar Dashas (sub-periods) that provide more precise timing for life events. Understanding your current Dasha period helps predict opportunities, challenges, and significant milestones in career, marriage, and health.</p>

          <h3>Frequently Asked Questions About Kundli</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>Is the free Kundli generator accurate?</h4>
              <p>Yes, AstroEpanchang uses precise Swiss Ephemeris calculations with Lahiri Ayanamsa — the same standard used by professional astrologers in India. Accuracy depends on entering the correct birth time, as even a few minutes difference can change the Lagna (Ascendant) and Dasha period.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What information do I need to generate my Kundli?</h4>
              <p>You need your exact date of birth, time of birth (as accurate as possible — hospital records are best), and place of birth (city/town). The birth place is used to determine geographic coordinates and timezone for precise planetary calculations.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is the difference between Rasi chart and Navamsa chart?</h4>
              <p>The Rasi chart (D-1) is the primary birth chart showing planetary positions at birth. The Navamsa chart (D-9) divides each sign into 9 equal parts and is used for marriage compatibility and spiritual analysis. Both charts are equally important in Vedic astrology — the Rasi chart shows potential, while the Navamsa reveals how that potential manifests in life.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is Manglik Dosha in a Kundli?</h4>
              <p>Manglik Dosha (Kuja Dosha) occurs when Mars is placed in the 1st, 2nd, 4th, 7th, 8th, or 12th house of the birth chart. It is believed to affect marriage harmony. Approximately 50% of people have some form of Manglik Dosha. When both partners have Manglik Dosha, the effects are believed to neutralize each other.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Can I get a Kundli without knowing my exact birth time?</h4>
              <p>A partial chart can be prepared using only date and place of birth, but it will lack accurate Lagna, house placements, and precise Dasha starting point. For complete and reliable predictions, the exact birth time is essential. If unknown, an experienced astrologer can perform birth time rectification using key life events.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is a Nakshatra and why is it important?</h4>
              <p>Nakshatras are 27 lunar mansions used in Vedic astrology, each spanning 13°20' of the zodiac. Your birth Nakshatra (the constellation where the Moon was placed at birth) determines your Vimshottari Dasha sequence, personality traits, and compatibility with other Nakshatras. It is one of the most fundamental elements in Vedic astrology for marriage matching, naming ceremonies, and spiritual practices.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kundli;
